import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { computeFrameTextBox } from '../../shared/lib/postLayout';
import { calculateOptimalFontSize, wrapText } from '../../shared/lib';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../shared/config/routes';
import { useAccountStore } from '../../entities/account';
import { useTemplateStore } from '../../entities/template';
import type { Template } from '../../entities/template';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner@2.0.3';
import { confirmDialog } from '../../shared/ui/ConfirmHost';
import { TopbarActions } from '../../shared/ui/TopbarSlot';
import { api } from '../../shared/api/client';
import { templatesApi } from '../../shared/api/templates.api';
import { PostTemplate, PostTemplateSettings } from '../../widgets/post-template';
import { ReelPreview } from '../../widgets/reel-preview';
import type { FontAxis } from '../../shared/lib/variableFontUtils';
import { PreviewCaption } from './PreviewCaption';
import { useTemplateForEdit } from './useTemplateForEdit';
import { useAccountTemplates } from './useAccountTemplates';
import { useWebCodecsProcessor } from '../../shared/lib/webcodecs/useWebCodecsProcessor';
import { buildOverlayParams } from '../../shared/lib/webcodecs/buildOverlayParams';
import { resolveEffectiveFontSize } from '../../shared/lib/video';
import { exportTemplateToBlob } from '../../features/template-export/lib/exportTemplate';

type TemplateTab = 'reel' | 'post';

/**
 * Отпечаток настроек для «есть ли несохранённое»: файлы — по имени/размеру/
 * дате (JSON.stringify дал бы {}), оси шрифта — не настройка, их выводит сам
 * шрифт после загрузки.
 */
function settingsFingerprint(...parts: unknown[]): string {
  return JSON.stringify(parts, (key, value) => {
    if (key === 'fontAxes') return undefined;
    if (typeof File !== 'undefined' && value instanceof File) return `file:${value.name}:${value.size}:${value.lastModified}`;
    return value;
  });
}

interface SettingsData {
  formData: {
    accountId: string;
    backgroundColor: string;
    useGradient: boolean;
    backgroundColorEnd: string;
    backgroundAlpha: number;
    backgroundAlphaEnd: number;
    textColor: string;
    useTwoColors: boolean;
    textColorSecondary: string;
    textUppercase: boolean;
    textAlign: 'left' | 'center' | 'right';
    textBoxStyle: 'rounded' | 'square' | 'square-flush' | 'square-flush-right';
    textBoxAngle: number;
    textBoxRadius: number;
    textBoxPadding: number;
    textBoxPaddingTop: number;
    textBoxPaddingLeft: number;
    textBoxPaddingRight: number;
    fontBold: boolean;
    showLine: boolean;
    lineColor: string;
    lineHeight: number;
    linePosition: 'top' | 'bottom';
    textShadowEnabled: boolean;
    textShadowColor: string;
    textShadowBlur: number;
    textShadowWidth: number;
    textSoftShadowEnabled: boolean;
    textSoftShadowColor: string;
    textSoftShadowBlur: number;
    textSoftShadowOffsetX: number;
    textSoftShadowOffsetY: number;
    textSoftShadowOpacity: number;
    textShadowOpacity: number;
    applyFontEffects: boolean;
    logoSize?: number;
    textLineSpacing?: number;
    fontSize?: number;
    postLayout: 'bottom-bar' | 'top-card' | 'frame';
    accentColor: string;
    cardColor: string;
    cardInset: number;
    bandHeight: number;
    frameUrl?: string;
    frameTextX: number;
    frameTextY: number;
    frameTextWidth: number;
    frameTextVAlign: 'top' | 'center' | 'bottom';
    reelBackgroundUrl?: string;
  };
  logoFile: File | null;
  logoPreview: string;
  logoPosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
  fontFile: File | null;
  fontFamily: string;
  fontName: string;
  fontUrl: string;
  fontAxes: FontAxis[];
  fontVariationSettings: Record<string, number>;
  frameFile: File | null;
  framePreview: string;
  reelBackgroundFile: File | null;
  reelBackgroundPreview: string;
}

function defaultSettings(accountId: string): SettingsData {
  return {
    formData: {
      accountId,
      backgroundColor: '#FFD700',
      useGradient: false,
      backgroundColorEnd: '#FFD700',
      backgroundAlpha: 1,
      backgroundAlphaEnd: 1,
      textColor: '#000000',
      useTwoColors: false,
      textColorSecondary: '#FFFFFF',
      textUppercase: false,
      textAlign: 'center',
      textBoxStyle: 'rounded',
      textBoxAngle: 0,
      textBoxRadius: 30,
      textBoxPadding: 12,
      textBoxPaddingTop: 12,
      textBoxPaddingLeft: 12,
      textBoxPaddingRight: 12,
      fontBold: false,
      showLine: false,
      lineColor: '#000000',
      lineHeight: 1,
      linePosition: 'top',
      textShadowEnabled: false,
      textShadowColor: '#000000',
      textShadowBlur: 4,
      textShadowWidth: 4,
      textSoftShadowEnabled: false,
      textSoftShadowColor: '#000000',
      textSoftShadowBlur: 8,
      textSoftShadowOffsetX: 2,
      textSoftShadowOffsetY: 4,
      textSoftShadowOpacity: 100,
      textShadowOpacity: 100,
      applyFontEffects: true,
      postLayout: 'bottom-bar',
      accentColor: '#FFD700',
      cardColor: '#FFFFFF',
      cardInset: 32,
      bandHeight: 0.22,
      frameUrl: undefined,
      frameTextX: 0.5,
      frameTextY: 0.12,
      frameTextWidth: 0.8,
      frameTextVAlign: 'top',
      reelBackgroundUrl: undefined,
    },
    logoFile: null,
    logoPreview: '',
    logoPosition: 'top-left',
    fontFile: null,
    fontFamily: 'Arial',
    fontName: 'Arial',
    fontUrl: '',
    fontAxes: [],
    fontVariationSettings: {},
    frameFile: null,
    framePreview: '',
    reelBackgroundFile: null,
    reelBackgroundPreview: '',
  };
}

// Map a loaded Template (or null) to the PostTemplateSettings `initialData` shape.
function toInitialData(t: Template | null, accountId: string) {
  if (!t) {
    return {
      accountId,
      backgroundColor: '#FFD700',
      useGradient: false,
      backgroundColorEnd: '#FFD700',
      backgroundAlpha: 1,
      backgroundAlphaEnd: 1,
      textColor: '#000000',
      useTwoColors: false,
      textColorSecondary: '#FFFFFF',
      logoUrl: undefined,
      logoPosition: 'top-left' as const,
      fontFamily: 'Arial',
      fontName: 'Arial',
      fontUrl: '',
      textUppercase: false,
      textAlign: 'center' as const,
      textBoxStyle: 'rounded' as const,
      textBoxAngle: 0,
      textBoxRadius: 30,
      textBoxPadding: 12,
      textBoxPaddingTop: 12,
      textBoxPaddingLeft: 12,
      textBoxPaddingRight: 12,
      fontBold: false,
      showLine: false,
      lineColor: '#000000',
      lineHeight: 1,
      linePosition: 'top' as const,
      textShadowEnabled: false,
      textShadowColor: '#000000',
      textShadowBlur: 4,
      textShadowWidth: 4,
      textSoftShadowEnabled: false,
      textSoftShadowColor: '#000000',
      textSoftShadowBlur: 8,
      textSoftShadowOffsetX: 2 as const,
      textSoftShadowOffsetY: 4 as const,
      textSoftShadowOpacity: 100 as const,
      textShadowOpacity: 100 as const,
      applyFontEffects: true as const,
      postLayout: 'bottom-bar' as const,
      accentColor: '#FFD700',
      cardColor: '#FFFFFF',
      cardInset: 32,
      bandHeight: 0.22,
      frameUrl: undefined,
      frameTextX: 0.5,
      frameTextY: 0.12,
      frameTextWidth: 0.8,
      frameTextVAlign: 'top',
      reelBackgroundUrl: undefined,
    };
  }
  return {
    // ВАЖНО: всегда текущий аккаунт (не accountId шаблона-источника при копировании).
    accountId,
    backgroundColor: t.backgroundColor,
    useGradient: t.useGradient,
    backgroundColorEnd: t.backgroundColorEnd,
    backgroundAlpha: t.backgroundAlpha,
    backgroundAlphaEnd: t.backgroundAlphaEnd,
    textColor: t.textColor,
    useTwoColors: t.useTwoColors,
    textColorSecondary: t.textColorSecondary,
    logoUrl: t.logoUrl,
    logoPosition: t.logoPosition,
    fontFamily: t.fontFamily,
    fontName: t.fontName,
    fontUrl: t.fontUrl,
    textUppercase: t.textUppercase,
    textAlign: t.textAlign,
    textBoxStyle: t.textBoxStyle ?? 'rounded',
    textBoxAngle: t.textBoxAngle ?? 0,
    textBoxRadius: t.textBoxRadius ?? 30,
    textBoxPadding: t.textBoxPadding ?? 12,
    textBoxPaddingTop: t.textBoxPaddingTop ?? t.textBoxPadding ?? 12,
    textBoxPaddingLeft: t.textBoxPaddingLeft ?? t.textBoxPadding ?? 12,
    textBoxPaddingRight: t.textBoxPaddingRight ?? t.textBoxPadding ?? 12,
    fontBold: t.fontBold,
    fontAxes: t.fontAxes,
    fontVariationSettings: t.fontVariationSettings,
    showLine: t.showLine,
    lineColor: t.lineColor,
    lineHeight: t.lineHeight,
    linePosition: t.linePosition,
    textShadowEnabled: t.textShadowEnabled,
    applyFontEffects: t.applyFontEffects ?? true,
    textShadowColor: t.textShadowColor,
    textShadowBlur: t.textShadowBlur,
    textShadowWidth: t.textShadowWidth,
    textSoftShadowEnabled: t.textSoftShadowEnabled,
    textSoftShadowColor: t.textSoftShadowColor,
    textSoftShadowBlur: t.textSoftShadowBlur,
    textSoftShadowOffsetX: t.textSoftShadowOffsetX,
    textSoftShadowOffsetY: t.textSoftShadowOffsetY,
    textSoftShadowOpacity: t.textSoftShadowOpacity,
    textShadowOpacity: t.textShadowOpacity,
    logoSize: t.logoSize,
    textLineSpacing: t.textLineSpacing,
    fontSize: t.fontSize,
    postLayout: t.postLayout,
    accentColor: t.accentColor,
    cardColor: t.cardColor,
    cardInset: t.cardInset,
    bandHeight: t.bandHeight,
    frameUrl: t.frameUrl,
    frameTextX: t.frameTextX,
    frameTextY: t.frameTextY,
    frameTextWidth: t.frameTextWidth,
    frameTextVAlign: t.frameTextVAlign,
    reelBackgroundUrl: t.reelBackgroundUrl,
  } as any;
}

export function TemplatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const accountIdFromUrl = searchParams.get('accountId');
  // ?type=post — открыть сразу таб поста (из «Шаблонов оформления»).
  const typeFromUrl = searchParams.get('type');
  // Куда вернуться после «Сохранить»: откуда пришли, иначе — к списку шаблонов.
  const returnTo = (location.state as { from?: string } | null)?.from ?? `${ROUTES.ACCOUNTS}/templates`;
  const accounts = useAccountStore((state) => state.accounts);
  const createTemplate = useTemplateStore((state) => state.createTemplate);
  const updateTemplateApi = useTemplateStore((state) => state.updateTemplate);
  const deleteTemplateApi = useTemplateStore((state) => state.deleteTemplate);

  // В edit-режиме :id — резолвим accountId из шаблона; в new-режиме — из query.
  const { template: editTemplate, isLoading: isEditLoading } = useTemplateForEdit(id);
  const accountId = accountIdFromUrl || editTemplate?.accountId || '';
  const currentAccount = accounts.find((a) => a.id === accountId);
  const currentAccountName = currentAccount?.username || currentAccount?.name || '';

  // Грузим оба шаблона аккаунта (+ ленивая миграция «дублировать в оба»).
  const { reel, post, isLoading: tplLoading } = useAccountTemplates(accountId || undefined);

  const { process: processReel } = useWebCodecsProcessor();

  const [activeTab, setActiveTab] = useState<TemplateTab>(typeFromUrl === 'post' ? 'post' : 'reel');
  // Старые ссылки на правку без ?type — таб по типу открытого шаблона.
  const tabFromTemplateRef = useRef(false);
  useEffect(() => {
    if (typeFromUrl || tabFromTemplateRef.current || !editTemplate) return;
    tabFromTemplateRef.current = true;
    if ((editTemplate as any).type === 'post') setActiveTab('post');
  }, [editTemplate, typeFromUrl]);
  // Копирование настроек с другого аккаунта: подменяет initialData форм и ремонтит их.
  const [copiedReel, setCopiedReel] = useState<Template | null>(null);
  const [copiedPost, setCopiedPost] = useState<Template | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [isCopying, setIsCopying] = useState(false);
  const [copySearch, setCopySearch] = useState('');

  // Отдельное состояние на каждый таб.
  const [reelSettings, setReelSettings] = useState<SettingsData>(() => defaultSettings(accountIdFromUrl || ''));
  const [postSettings, setPostSettings] = useState<SettingsData>(() => defaultSettings(accountIdFromUrl || ''));

  // Тестовые медиа для превью.
  const [previewImage, setPreviewImage] = useState<string>('');
  const [postSampleText, setPostSampleText] = useState<string>('Пример текста поста');
  // QA: рендер поста через тот же экспорт, что и публикация.
  const [postGenBusy, setPostGenBusy] = useState(false);
  const postTemplateRef = useRef<HTMLDivElement>(null);
  // Инициализируем contentEditable один раз (без React-children), чтобы каретка не прыгала.
  const initFrameText = useCallback((el: HTMLDivElement | null) => {
    if (el && !el.dataset.init) { el.textContent = postSampleText; el.dataset.init = '1'; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Offscreen ctx для замера текста (тот же расчёт что в canvas-burn → 1:1 превью).
  const measureCtx = useMemo(() => document.createElement('canvas').getContext('2d'), []);
  const [reelTestVideo, setReelTestVideo] = useState<File | null>(null);
  const [reelTextPosition, setReelTextPosition] = useState<import('../../widgets/reel-create-form/lib/buildRenderTasks').TextPosition | null>(null);
  const [reelTextAnchor, setReelTextAnchor] = useState<{ centerX: number; bottom: number }>({ centerX: 0, bottom: 380 });
  const [reelGenPct, setReelGenPct] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Несохранённые изменения — по данным, а не по кликам (02.11.2026): раньше
  // «грязным» делал любой клик в форме, даже переключение Reels/Пост.
  // Точка отсчёта — настройки в момент первого касания формы: всё, что формы
  // и шрифты донастроили сами при загрузке, к правкам не относится.
  const fingerprint = settingsFingerprint(reelSettings, postSettings, reelTextAnchor);
  const fingerprintRef = useRef(fingerprint);
  fingerprintRef.current = fingerprint;
  const [baseline, setBaseline] = useState<string | null>(null);
  useEffect(() => {
    const freeze = (e: Event) => {
      if ((e.target as HTMLElement)?.closest?.('#template-form')) {
        setBaseline((b) => b ?? fingerprintRef.current);
      }
    };
    document.addEventListener('pointerdown', freeze, true);
    document.addEventListener('keydown', freeze, true);
    return () => {
      document.removeEventListener('pointerdown', freeze, true);
      document.removeEventListener('keydown', freeze, true);
    };
  }, []);
  const dirty = baseline !== null && fingerprint !== baseline;

  // Предупреждение при закрытии/обновлении вкладки — только системным окном браузера.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Перехват кликов по ссылкам (in-app навигация) при несохранённых изменениях:
  // переход откладываем, спрашиваем своим окном и идём сами.
  useEffect(() => {
    if (!dirty) return;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('#') || a.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const url = new URL(a.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const to = url.pathname + url.search + url.hash;
      if (window.location.pathname + window.location.search === url.pathname + url.search) return;
      e.preventDefault();
      e.stopPropagation();
      void confirmDialog({
        title: 'Уйти без сохранения?',
        description: 'Изменения оформления не сохранены и пропадут.',
        confirmText: 'Уйти',
        destructive: true,
      }).then((ok) => {
        if (ok) {
          setBaseline(null);
          navigate(to);
        }
      });
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [dirty, navigate]);

  const effReel = copiedReel ?? reel;
  const effPost = copiedPost ?? post;
  // Выравнивание — своё на таб: reel по умолчанию слева, post по центру.
  const reelInitial = {
    ...toInitialData(effReel, accountId),
    textAlign: (effReel as any)?.textAlign ?? 'left',
    textBoxStyle: (effReel as any)?.textBoxStyle ?? 'rounded',
    textBoxAngle: (effReel as any)?.textBoxAngle ?? 0,
    textBoxRadius: (effReel as any)?.textBoxRadius ?? 30,
    textBoxPadding: (effReel as any)?.textBoxPadding ?? 12,
    textBoxPaddingTop: (effReel as any)?.textBoxPaddingTop ?? (effReel as any)?.textBoxPadding ?? 12,
    textBoxPaddingLeft: (effReel as any)?.textBoxPaddingLeft ?? (effReel as any)?.textBoxPadding ?? 12,
    textBoxPaddingRight: (effReel as any)?.textBoxPaddingRight ?? (effReel as any)?.textBoxPadding ?? 12,
  };
  // Лого-картинка и шрифт у поста — общие из reel. Выравнивание — своё (центр по умолчанию).
  const postInitial = {
    ...toInitialData(effPost, accountId),
    textAlign: (effPost as any)?.textAlign ?? 'center',
    textBoxStyle: (effPost as any)?.textBoxStyle ?? 'rounded',
    textBoxAngle: (effPost as any)?.textBoxAngle ?? 0,
    textBoxRadius: (effPost as any)?.textBoxRadius ?? 30,
    textBoxPadding: (effPost as any)?.textBoxPadding ?? 12,
    textBoxPaddingTop: (effPost as any)?.textBoxPaddingTop ?? (effPost as any)?.textBoxPadding ?? 12,
    textBoxPaddingLeft: (effPost as any)?.textBoxPaddingLeft ?? (effPost as any)?.textBoxPadding ?? 12,
    textBoxPaddingRight: (effPost as any)?.textBoxPaddingRight ?? (effPost as any)?.textBoxPadding ?? 12,
    applyFontEffects: (effPost as any)?.applyFontEffects ?? true,
    logoUrl: effReel?.logoUrl ?? (effPost as any)?.logoUrl,
    fontFamily: effReel?.fontFamily ?? (effPost as any)?.fontFamily,
    fontName: effReel?.fontName ?? (effPost as any)?.fontName,
    fontUrl: effReel?.fontUrl ?? (effPost as any)?.fontUrl,
    fontAxes: effReel?.fontAxes ?? (effPost as any)?.fontAxes,
    fontVariationSettings: effReel?.fontVariationSettings ?? (effPost as any)?.fontVariationSettings,
  };

  // Гидрация reel-позиции текста из загруженного шаблона (один раз на template id).
  // Храним в отдельном state reelTextAnchor, а не в reelSettings.formData: child
  // PostTemplateSettings пересобирает свой formData из явного списка полей и при
  // каждом onDataChange затёр бы эти ключи. Отдельный state этого избегает.
  const hydratedReelPosRef = useRef<string | null>(null);
  useEffect(() => {
    const key = (effReel as any)?.id ?? null;
    if (hydratedReelPosRef.current === key) return;
    hydratedReelPosRef.current = key;
    setReelTextAnchor({
      centerX: (effReel as any)?.reelTextCenterX ?? 0,
      bottom: (effReel as any)?.reelTextBottom ?? 380,
    });
  }, [effReel]);

  // 1:1 фит текста в кадре: тот же box + тот же расчёт что в canvas-burn. Учитывает ручной
  // размер шрифта. overflow = текст выходит за пределы канваса (по вертикали).
  const frameFit = useMemo(() => {
    const box = computeFrameTextBox({
      width: 1080, height: 1350,
      frameTextX: postSettings.formData.frameTextX,
      frameTextY: postSettings.formData.frameTextY,
      frameTextWidth: postSettings.formData.frameTextWidth,
    });
    const font = reelSettings.fontFamily || 'Arial';
    let fontSize = 40;
    let lines: string[] = [];
    if (measureCtx && postSampleText.trim()) {
      const processed = postSettings.formData.textUppercase ? postSampleText.toUpperCase() : postSampleText;
      const manual = postSettings.formData.fontSize;
      if (manual && manual > 0) {
        fontSize = manual;
        lines = wrapText(processed, box.maxWidth, manual, font, measureCtx);
      } else {
        const r = calculateOptimalFontSize(processed, box.maxWidth, box.maxHeight, font, measureCtx, 80);
        fontSize = r.fontSize;
        lines = r.lines;
      }
    }
    const ls = reelSettings.formData.textLineSpacing && reelSettings.formData.textLineSpacing > 0
      ? reelSettings.formData.textLineSpacing : 1.2;
    const blockH = lines.length * fontSize * ls;
    const vAlign = postSettings.formData.frameTextVAlign;
    const top = vAlign === 'center' ? box.cy - blockH / 2 : vAlign === 'bottom' ? box.cy - blockH : box.cy;
    const overflow = lines.length > 0 && (top < 0 || top + blockH > 1350);
    return { box, fontSize, overflow };
  }, [
    postSampleText,
    postSettings.formData.frameTextX,
    postSettings.formData.frameTextY,
    postSettings.formData.frameTextWidth,
    postSettings.formData.fontSize,
    postSettings.formData.frameTextVAlign,
    postSettings.formData.textUppercase,
    reelSettings.formData.textLineSpacing,
    reelSettings.fontFamily,
    measureCtx,
  ]);

  const handlePreviewImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Пожалуйста, выберите изображение');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setPreviewImage(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  // QA: рендер тестового фото через exportTemplateToBlob с ТЕКУЩИМИ настройками поста.
  // Источники полей зеркалят пропсы <PostTemplate> (layout — postSettings, текст — reelSettings).
  const handleGeneratePost = async () => {
    if (!previewImage) return;
    setPostGenBusy(true);
    try {
      const p = postSettings.formData;
      const r = reelSettings.formData;
      const fx = p.applyFontEffects === false;
      const sourceWidth = postTemplateRef.current?.getBoundingClientRect().width || 400;
      const blob = await exportTemplateToBlob({
        backgroundColor: p.backgroundColor, useGradient: p.useGradient,
        backgroundColorEnd: p.backgroundColorEnd, backgroundAlpha: p.backgroundAlpha,
        backgroundAlphaEnd: p.backgroundAlphaEnd,
        textColor: r.textColor, useTwoColors: r.useTwoColors, textColorSecondary: r.textColorSecondary,
        fontFamily: reelSettings.fontFamily, fontUrl: reelSettings.fontUrl, fontName: reelSettings.fontName,
        fontSize: p.fontSize, fontBold: r.fontBold, fontVariationSettings: reelSettings.fontVariationSettings,
        logoUrl: reelSettings.logoPreview, logoPosition: postSettings.logoPosition as any, logoSize: p.logoSize,
        textLineSpacing: r.textLineSpacing,
        imageUrl: previewImage, text: postSampleText, textUppercase: p.textUppercase, textAlign: p.textAlign,
        showLine: p.showLine, lineColor: p.lineColor, lineHeight: p.lineHeight, linePosition: p.linePosition,
        textShadowEnabled: fx ? false : r.textShadowEnabled, textShadowColor: r.textShadowColor,
        textShadowBlur: r.textShadowBlur, textShadowWidth: r.textShadowWidth, textShadowOpacity: r.textShadowOpacity,
        textSoftShadowEnabled: fx ? false : r.textSoftShadowEnabled, textSoftShadowColor: r.textSoftShadowColor,
        textSoftShadowBlur: r.textSoftShadowBlur, textSoftShadowOffsetX: r.textSoftShadowOffsetX,
        textSoftShadowOffsetY: r.textSoftShadowOffsetY, textSoftShadowOpacity: r.textSoftShadowOpacity,
        postLayout: p.postLayout, accentColor: p.accentColor, cardColor: p.cardColor,
        cardInset: p.cardInset, bandHeight: p.bandHeight,
        frameUrl: postSettings.framePreview || p.frameUrl,
        frameTextX: p.frameTextX, frameTextY: p.frameTextY, frameTextWidth: p.frameTextWidth,
        frameTextVAlign: p.frameTextVAlign,
        sourceWidth,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'post.png'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('generate post failed', e);
      toast.error('Не удалось сгенерировать пост');
    } finally {
      setPostGenBusy(false);
    }
  };

  const handleReelVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Пожалуйста, выберите видео');
        return;
      }
      setReelTestVideo(file);
    }
  };

  const handleReelTextPositionChange = (pos: { x: number; y: number; previewWidth: number; previewHeight: number; textBoxWidth: number; bottom: number; lines: string[]; fontSize: number; centerXFrac: number; fromDrag: boolean }) => {
    setReelTextPosition(pos);
    if (pos.fromDrag) {
      setReelTextAnchor({ centerX: pos.centerXFrac, bottom: pos.bottom });
    }
  };

  const handleGenerateReel = async () => {
    if (!reelTestVideo || !reelTextPosition) return;
    setReelGenPct(0);
    try {
      const f = reelSettings.formData;
      const logoArrayBuffer = reelSettings.logoFile
        ? await reelSettings.logoFile.arrayBuffer()
        : (reelSettings.logoPreview
            ? await fetch(reelSettings.logoPreview).then(r => (r.ok ? r.arrayBuffer() : null)).catch(() => null)
            : null);
      const overlay = await buildOverlayParams({
        template: {
          backgroundColor: f.backgroundColor, backgroundAlpha: f.backgroundAlpha,
          textColor: f.textColor, textColorSecondary: f.textColorSecondary, useTwoColors: f.useTwoColors,
          fontFamily: reelSettings.fontFamily, fontUrl: reelSettings.fontUrl,
          fontVariationSettings: reelSettings.fontVariationSettings, textUppercase: f.textUppercase,
          textBoxStyle: f.textBoxStyle, textBoxAngle: f.textBoxAngle, textBoxRadius: f.textBoxRadius,
          textBoxPadding: f.textBoxPadding, textBoxPaddingTop: f.textBoxPaddingTop,
          textBoxPaddingLeft: f.textBoxPaddingLeft, textBoxPaddingRight: f.textBoxPaddingRight,
          textShadowEnabled: f.textShadowEnabled, textShadowColor: f.textShadowColor,
          textShadowBlur: f.textShadowBlur, textShadowWidth: f.textShadowWidth, textShadowOpacity: f.textShadowOpacity,
          textSoftShadowEnabled: f.textSoftShadowEnabled, textSoftShadowColor: f.textSoftShadowColor,
          textSoftShadowBlur: f.textSoftShadowBlur, textSoftShadowOffsetX: f.textSoftShadowOffsetX,
          textSoftShadowOffsetY: f.textSoftShadowOffsetY, textSoftShadowOpacity: f.textSoftShadowOpacity,
          logoPosition: reelSettings.logoPosition as any,
          frameUrl: reelSettings.framePreview || f.frameUrl,
          reelBackgroundUrl: f.reelBackgroundUrl,
          textAlign: f.textAlign, logoSize: f.logoSize, textLineSpacing: f.textLineSpacing,
          reelTextCenterX: reelTextAnchor.centerX,
        },
        text: 'Пример заголовка',
        textPosition: reelTextPosition,
        fontSize: resolveEffectiveFontSize(f.fontSize ?? null),
        logoArrayBuffer,
      });
      const blob = await processReel({ videoFile: reelTestVideo, overlay, onProgress: setReelGenPct });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'reel.mp4'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('generate reel failed', e);
      toast.error('Не удалось сгенерировать рилс');
    } finally {
      setReelGenPct(null);
    }
  };

  // Общие поля шаблона из настроек таба (без type/шрифта/лого-overrides).
  const buildBaseData = (s: SettingsData, frameUrl?: string, reelBackgroundUrl?: string) => ({
    accountId: s.formData.accountId,
    backgroundColor: s.formData.backgroundColor,
    useGradient: s.formData.useGradient,
    backgroundColorEnd: s.formData.backgroundColorEnd,
    backgroundAlpha: s.formData.backgroundAlpha,
    backgroundAlphaEnd: s.formData.backgroundAlphaEnd,
    textColor: s.formData.textColor,
    useTwoColors: s.formData.useTwoColors,
    textColorSecondary: s.formData.textColorSecondary,
    logoPosition: s.logoPosition,
    textUppercase: s.formData.textUppercase,
    textAlign: s.formData.textAlign,
    textBoxStyle: s.formData.textBoxStyle,
    textBoxAngle: s.formData.textBoxAngle,
    textBoxRadius: s.formData.textBoxRadius,
    textBoxPadding: s.formData.textBoxPadding,
    textBoxPaddingTop: s.formData.textBoxPaddingTop,
    textBoxPaddingLeft: s.formData.textBoxPaddingLeft,
    textBoxPaddingRight: s.formData.textBoxPaddingRight,
    fontBold: s.formData.fontBold,
    showLine: s.formData.showLine,
    lineColor: s.formData.lineColor,
    lineHeight: s.formData.lineHeight,
    linePosition: s.formData.linePosition,
    textShadowEnabled: s.formData.textShadowEnabled,
    applyFontEffects: s.formData.applyFontEffects,
    textShadowColor: s.formData.textShadowColor,
    textShadowBlur: s.formData.textShadowBlur,
    textShadowWidth: s.formData.textShadowWidth,
    textSoftShadowEnabled: s.formData.textSoftShadowEnabled,
    textSoftShadowColor: s.formData.textSoftShadowColor,
    textSoftShadowBlur: s.formData.textSoftShadowBlur,
    textSoftShadowOffsetX: s.formData.textSoftShadowOffsetX,
    textSoftShadowOffsetY: s.formData.textSoftShadowOffsetY,
    textSoftShadowOpacity: s.formData.textSoftShadowOpacity,
    textShadowOpacity: s.formData.textShadowOpacity,
    logoSize: s.formData.logoSize,
    textLineSpacing: s.formData.textLineSpacing,
    // null (не undefined): "авто" = fontSize пустой. undefined выпал бы из JSON,
    // PUT смёржил бы {...existing, ...body} и старый числовой размер остался бы.
    // null перетирает → "авто" реально сохраняется. (как с лого/каркасом выше).
    fontSize: s.formData.fontSize ?? null,
    postLayout: s.formData.postLayout,
    accentColor: s.formData.accentColor,
    cardColor: s.formData.cardColor,
    cardInset: s.formData.cardInset,
    bandHeight: s.formData.bandHeight,
    frameUrl,
    frameTextX: s.formData.frameTextX,
    frameTextY: s.formData.frameTextY,
    frameTextWidth: s.formData.frameTextWidth,
    frameTextVAlign: s.formData.frameTextVAlign,
    reelBackgroundUrl,
  });

  // Upsert: один шаблон на (accountId, type). Если есть дубли — обновляем самый свежий,
  // остальные дубли того же типа удаляем (схождение к одному). accountId/type — авторитетно.
  const upsertTemplate = async (accountId: string, type: TemplateTab, data: any) => {
    const payload = { ...data, accountId, type };
    const all = await templatesApi.getAllByAccountId(accountId);
    const sameType = all
      .filter((t) => ((t as any).type ?? 'reel') === type)
      .sort((a, b) => String((b as any).updatedAt || '').localeCompare(String((a as any).updatedAt || '')));
    if (sameType.length === 0) {
      await createTemplate(payload);
      return;
    }
    const keep = sameType[0];
    const saved = await updateTemplateApi(keep.id, payload);
    // updateTemplate-экшен мёржит по id через .map: если стор был устаревшим и
    // keep.id в нём нет, обновлённый шаблон не попал бы в стор. Гарантируем наличие.
    if (saved && !useTemplateStore.getState().templates.some((t) => t.id === saved.id)) {
      useTemplateStore.getState().addTemplate(saved);
    }
    // Удаляем устаревшие дубли того же типа.
    // ВАЖНО: через store-экшен deleteTemplate (не raw templatesApi.delete),
    // иначе дубль остаётся в zustand-сторе. Публикация выбирает шаблон через
    // templates.find(...accountId, type==='reel') = ПЕРВОЕ совпадение; залежавшийся
    // дубль со старым цветом перехватывал бы find → старый цвет в видео.
    for (const dup of sameType.slice(1)) {
      try { await deleteTemplateApi(dup.id); } catch { /* ignore */ }
    }
  };

  // Сохраняет текущий аккаунт (reel+post). Возвращает true при успехе.
  const saveCurrent = async (): Promise<boolean> => {
    // Аккаунт задаёт страница (шапка «Шаблон @X»), а не выпадашка внутри формы:
    // раньше её можно было переключить и перезаписать чужое оформление.
    if (!accountId) {
      toast.error('Выберите аккаунт');
      return false;
    }
    setIsUploading(true);
    try {
      // ── REEL: грузим лого/шрифт, сохраняем ──
      // logoPreview = текущая картинка в форме (своя ИЛИ скопированная с источника).
      // Пусто = юзер убрал лого → шлём '' (не undefined: PUT мёржит {...existing, ...body},
      // undefined выпал бы из JSON и старое лого осталось бы). '' перетирает.
      // Новый файл (logoFile) перекроет её загруженным URL ниже.
      let reelLogoUrl: string = reelSettings.logoPreview || '';
      if (reelSettings.logoFile) {
        const fd = new FormData();
        fd.append('file', reelSettings.logoFile);
        fd.append('accountId', accountId);
        reelLogoUrl = (await api.postFormData<{ url: string }>('/upload/logo', fd)).url;
      }
      let reelFontUrl = reelSettings.fontUrl || undefined;
      if (reelSettings.fontFile) {
        const fd = new FormData();
        fd.append('file', reelSettings.fontFile);
        reelFontUrl = (await api.postFormData<{ url: string }>('/fonts/upload', fd)).url;
      }
      // Каркас рилса (PNG 1080×1920).
      // ?? (не ||): пустая строка = каркас явно удалён через X, не откатываем на сохранённый.
      let reelFrameUrl = reelSettings.formData.frameUrl ?? effReel?.frameUrl;
      if (reelSettings.frameFile) {
        const fd = new FormData();
        fd.append('file', reelSettings.frameFile);
        fd.append('accountId', accountId);
        reelFrameUrl = (await api.postFormData<{ url: string }>('/upload/frame', fd)).url;
      }
      // Фон рилса (виден, когда видео меньше формата рилса).
      // ?? (не ||): пустая строка = фон явно удалён через X, не откатываем на сохранённый.
      let reelBackgroundUrl = reelSettings.formData.reelBackgroundUrl ?? effReel?.reelBackgroundUrl;
      if (reelSettings.reelBackgroundFile) {
        const fd = new FormData();
        fd.append('file', reelSettings.reelBackgroundFile);
        fd.append('accountId', accountId);
        reelBackgroundUrl = (await api.postFormData<{ url: string }>('/upload/frame', fd)).url;
      }
      await upsertTemplate(accountId, 'reel', {
        ...buildBaseData(reelSettings, reelFrameUrl, reelBackgroundUrl),
        fontFamily: reelSettings.fontFamily,
        fontName: reelSettings.fontName,
        fontUrl: reelFontUrl,
        fontAxes: reelSettings.fontAxes,
        fontVariationSettings: reelSettings.fontVariationSettings,
        logoUrl: reelLogoUrl,
        reelTextCenterX: reelTextAnchor.centerX ?? 0,
        reelTextBottom: reelTextAnchor.bottom ?? 380,
      });

      // ── POST: каркас грузим, шрифт+картинку лого берём из reel ──
      let postFrameUrl = postSettings.formData.frameUrl ?? effPost?.frameUrl;
      if (postSettings.frameFile) {
        const fd = new FormData();
        fd.append('file', postSettings.frameFile);
        fd.append('accountId', accountId);
        postFrameUrl = (await api.postFormData<{ url: string }>('/upload/frame', fd)).url;
      }
      await upsertTemplate(accountId, 'post', {
        ...buildBaseData(postSettings, postFrameUrl),
        // Шрифт + картинка лого + ВСЕ текстовые стили — из reel.
        fontFamily: reelSettings.fontFamily,
        fontName: reelSettings.fontName,
        fontUrl: reelFontUrl,
        fontAxes: reelSettings.fontAxes,
        fontVariationSettings: reelSettings.fontVariationSettings,
        logoUrl: reelLogoUrl, // картинка лого общая; позиция/размер — постовые (в buildBaseData)
        textColor: reelSettings.formData.textColor,
        useTwoColors: reelSettings.formData.useTwoColors,
        textColorSecondary: reelSettings.formData.textColorSecondary,
        // textUppercase НЕ наследуем из reel — регистр у поста свой (чекбокс в настройках поста).
        fontBold: reelSettings.formData.fontBold,
        textShadowEnabled: reelSettings.formData.textShadowEnabled,
        textShadowColor: reelSettings.formData.textShadowColor,
        textShadowBlur: reelSettings.formData.textShadowBlur,
        textShadowWidth: reelSettings.formData.textShadowWidth,
        textSoftShadowEnabled: reelSettings.formData.textSoftShadowEnabled,
        textSoftShadowColor: reelSettings.formData.textSoftShadowColor,
        textSoftShadowBlur: reelSettings.formData.textSoftShadowBlur,
        textSoftShadowOffsetX: reelSettings.formData.textSoftShadowOffsetX,
        textSoftShadowOffsetY: reelSettings.formData.textSoftShadowOffsetY,
        textSoftShadowOpacity: reelSettings.formData.textSoftShadowOpacity,
        textShadowOpacity: reelSettings.formData.textShadowOpacity,
        textLineSpacing: reelSettings.formData.textLineSpacing,
      });

      toast.success('Сохранено');
      return true;
    } catch (error) {
      console.error('Template save error:', error);
      toast.error(error instanceof Error ? error.message : 'Ошибка сохранения шаблона');
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isUploading) return;
    const ok = await saveCurrent();
    if (ok) {
      setBaseline(null);
      navigate(returnTo);
    }
  };

  // Взять reel+post шаблоны другого аккаунта в текущую форму (без записи; редактируй и сохраняй).
  const copyFromAccount = async (srcId: string) => {
    const src = accounts.find((a) => a.id === srcId);
    const ok = await confirmDialog({
      title: `Заменить Reels и Пост оформлением @${src?.username || src?.name || srcId}?`,
      description: 'Текущие настройки обоих форматов в форме заменятся. На сервер ничего не уйдёт, пока не нажмёте «Сохранить».',
      confirmText: 'Заменить',
    });
    if (!ok) return;
    // Касание выпадашки уже зафиксировало точку отсчёта; если нет — фиксируем
    // сейчас, чтобы копия считалась несохранённой правкой.
    setBaseline((b) => b ?? fingerprintRef.current);
    setIsCopying(true);
    try {
      const [srcReel, srcPost] = await Promise.all([
        templatesApi.getByAccountId(srcId, 'reel'),
        templatesApi.getByAccountId(srcId, 'post'),
      ]);
      setCopiedReel((srcReel as unknown as Template) || null);
      // Позиция текста рилса хранится отдельно — копируем её из источника тоже,
      // иначе остаётся расположение прежнего аккаунта.
      setReelTextAnchor({
        centerX: (srcReel as any)?.reelTextCenterX ?? 0,
        bottom: (srcReel as any)?.reelTextBottom ?? 380,
      });
      setCopiedPost((srcPost as unknown as Template) || null);
      setFormKey((k) => k + 1); // ремонтим формы с новыми initialData
      toast.success('Оформление скопировано в форму. Проверьте и сохраните.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось скопировать');
    } finally {
      setIsCopying(false);
    }
  };

  // Пока резолвим accountId/грузим шаблоны — спиннер.
  if ((id && isEditLoading) || (accountId && tplLoading)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} id="template-form">
      {/* «Сохранить» — в топбаре: на виду при любой прокрутке длинных настроек. */}
      <TopbarActions>
        <Button type="submit" form="template-form" disabled={isUploading}>
          {isUploading && <Loader2 className="animate-spin" />}
          {isUploading ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </TopbarActions>
      {isUploading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="flex items-center gap-3 rounded-lg bg-background px-6 py-4 shadow-lg">
            <Loader2 className="size-5 animate-spin text-primary" />
            <span className="text-sm">Сохранение шаблона…</span>
          </div>
        </div>
      )}
      {/* Шапка: чей это шаблон и что с ним можно сделать. Раньше сверху висела
          одинокая полоса «Скопировать настройки», и было непонятно, чьё это
          оформление вообще редактируется. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar className="size-10">
            <AvatarImage src={currentAccount?.profilePicture} alt={currentAccountName} />
            <AvatarFallback>{currentAccountName.slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold">
              Шаблон {currentAccountName ? `@${currentAccountName}` : 'оформления'}
            </h1>
            <p className="text-sm text-muted-foreground">
              Применяется ко всем публикациям этого аккаунта
            </p>
          </div>
        </div>

        <Select value="" onValueChange={(v) => v && copyFromAccount(v)} disabled={isCopying}
          onOpenChange={(o) => { if (!o) setCopySearch(''); }}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={isCopying ? 'Копируем…' : 'Скопировать с аккаунта'} />
          </SelectTrigger>
          <SelectContent>
            <div className="p-1.5">
              <input
                value={copySearch}
                onChange={(e) => setCopySearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Поиск аккаунта…"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                className="w-full rounded border px-2 py-1 text-sm"
              />
            </div>
            {accounts
              .filter((a) => a.id !== accountId)
              .filter((a) => (a.username || a.name || '').toLowerCase().includes(copySearch.toLowerCase()))
              .map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.username || a.name}</SelectItem>
              ))}
            {accounts.filter((a) => a.id !== accountId && (a.username || a.name || '').toLowerCase().includes(copySearch.toLowerCase())).length === 0 && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">Ничего не найдено</div>
            )}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TemplateTab)}>
        {/* Переключатель формата липнет под топбар: настройки длинные, и без
            него на середине списка не понять, что сейчас правишь. */}
        <TabsList className="sticky top-14 z-10 mb-4 grid w-full grid-cols-2">
          <TabsTrigger value="reel">Reels · 9:16</TabsTrigger>
          <TabsTrigger value="post">Пост · 1080×1350</TabsTrigger>
        </TabsList>

        {/* ПОСТ. forceMount — настройки остаются смонтированы между табами. */}
        <TabsContent value="post" forceMount className="data-[state=inactive]:hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Настройки шаблона поста</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <PostTemplateSettings
                    key={`post-${formKey}`}
                    initialData={postInitial}
                    onDataChange={setPostSettings}
                    hideTextControls
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="lg:sticky lg:top-[72px] lg:self-start">
              <CardHeader>
                <CardTitle>Превью поста (1080×1350)</CardTitle>
                <CardDescription>Нажмите на область, чтобы загрузить тестовое фото</CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePreviewImageChange}
                  id="previewImageUpload"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="mb-3 w-full"
                  onClick={() => document.getElementById('previewImageUpload')?.click()}
                >
                  {previewImage ? 'Заменить фото' : 'Загрузить тестовое фото'}
                </Button>
                {postSettings.formData.postLayout !== 'frame' && (
                  <textarea
                    value={postSampleText}
                    onChange={(e) => setPostSampleText(e.target.value)}
                    placeholder="Текст для превью"
                    rows={2}
                    className="mb-3 w-full resize-none rounded-md border px-3 py-2 text-sm"
                  />
                )}
                {postSettings.formData.postLayout === 'frame' && frameFit.overflow && (
                  <div className="mb-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                    ⚠️ Текст не вмещается в кадр — уменьши размер шрифта, увеличь ширину или подвинь «Текст Y».
                  </div>
                )}
                <div
                  ref={postTemplateRef}
                  className="relative rounded-lg overflow-hidden [&_[data-template-export]]:border-0"
                  style={{
                    containerType: 'inline-size',
                    backgroundColor: '#e5e7eb',
                    backgroundImage:
                      'linear-gradient(45deg,#cbd5e1 25%,transparent 25%),linear-gradient(-45deg,#cbd5e1 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#cbd5e1 75%),linear-gradient(-45deg,transparent 75%,#cbd5e1 75%)',
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0,0 10px,10px -10px,-10px 0',
                  }}
                >
                <PostTemplate
                  text={postSampleText}
                  fontSize={postSettings.formData.fontSize}
                  backgroundColor={postSettings.formData.backgroundColor}
                  useGradient={postSettings.formData.useGradient}
                  backgroundColorEnd={postSettings.formData.backgroundColorEnd}
                  backgroundAlpha={postSettings.formData.backgroundAlpha}
                  backgroundAlphaEnd={postSettings.formData.backgroundAlphaEnd}
                  textColor={reelSettings.formData.textColor}
                  useTwoColors={reelSettings.formData.useTwoColors}
                  textColorSecondary={reelSettings.formData.textColorSecondary}
                  fontFamily={reelSettings.fontFamily}
                  fontUrl={reelSettings.fontUrl}
                  logoUrl={reelSettings.logoPreview}
                  logoPosition={postSettings.logoPosition}
                  logoSize={postSettings.formData.logoSize}
                  imageUrl={previewImage}
                  onImageClick={() => document.getElementById('previewImageUpload')?.click()}
                  textUppercase={postSettings.formData.textUppercase}
                  textAlign={postSettings.formData.textAlign}
                  textLineSpacing={reelSettings.formData.textLineSpacing}
                  fontBold={reelSettings.formData.fontBold}
                  fontVariationSettings={reelSettings.fontVariationSettings}
                  showLine={postSettings.formData.showLine}
                  lineColor={postSettings.formData.lineColor}
                  lineHeight={postSettings.formData.lineHeight}
                  linePosition={postSettings.formData.linePosition}
                  textShadowEnabled={postSettings.formData.applyFontEffects === false ? false : reelSettings.formData.textShadowEnabled}
                  textShadowColor={reelSettings.formData.textShadowColor}
                  textShadowBlur={reelSettings.formData.textShadowBlur}
                  textShadowWidth={reelSettings.formData.textShadowWidth}
                  textSoftShadowEnabled={postSettings.formData.applyFontEffects === false ? false : reelSettings.formData.textSoftShadowEnabled}
                  textSoftShadowColor={reelSettings.formData.textSoftShadowColor}
                  textSoftShadowBlur={reelSettings.formData.textSoftShadowBlur}
                  textSoftShadowOffsetX={reelSettings.formData.textSoftShadowOffsetX}
                  textSoftShadowOffsetY={reelSettings.formData.textSoftShadowOffsetY}
                  textSoftShadowOpacity={reelSettings.formData.textSoftShadowOpacity}
                  textShadowOpacity={reelSettings.formData.textShadowOpacity}
                  postLayout={postSettings.formData.postLayout}
                  accentColor={postSettings.formData.accentColor}
                  cardColor={postSettings.formData.cardColor}
                  cardInset={postSettings.formData.cardInset}
                  bandHeight={postSettings.formData.bandHeight}
                  frameUrl={postSettings.framePreview || postSettings.formData.frameUrl}
                  frameTextX={postSettings.formData.frameTextX}
                  frameTextY={postSettings.formData.frameTextY}
                  frameTextWidth={postSettings.formData.frameTextWidth}
                  frameTextVAlign={postSettings.formData.frameTextVAlign}
                />
                {postSettings.formData.postLayout === 'frame' && (
                  <div
                    ref={initFrameText}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => setPostSampleText(e.currentTarget.textContent || '')}
                    className="absolute outline-none cursor-text"
                    style={{
                      left: `${(frameFit.box.cx / 1080) * 100}%`,
                      top: `${(frameFit.box.cy / 1350) * 100}%`,
                      width: `${(frameFit.box.maxWidth / 1080) * 100}%`,
                      transform: `translate(-50%, ${postSettings.formData.frameTextVAlign === 'center' ? '-50%' : postSettings.formData.frameTextVAlign === 'bottom' ? '-100%' : '0'})`,
                      fontFamily: reelSettings.fontFamily || 'Arial',
                      // Текст рисует canvas (1:1 с публикацией). Оверлей — только ввод/каретка.
                      color: 'transparent',
                      caretColor: reelSettings.formData.textColor,
                      textAlign: postSettings.formData.textAlign,
                      textTransform: postSettings.formData.textUppercase ? 'uppercase' : 'none',
                      // template-space px → cqw (контейнер шириной = 1080 template px = 100cqw)
                      fontSize: `${(frameFit.fontSize / 1080) * 100}cqw`,
                      lineHeight: postSettings.formData.textLineSpacing && postSettings.formData.textLineSpacing > 0
                        ? postSettings.formData.textLineSpacing : 1.2,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  />
                )}
                </div>
                <PreviewCaption
                  size="1080×1350"
                  fontName={reelSettings.fontName}
                  fontSize={postSettings.formData.fontSize}
                  backgroundAlpha={postSettings.formData.backgroundAlpha}
                  safeZoneHint="В ленте пост обрежется до квадрата — главное держите в центре кадра"
                />
                <div className="mt-3 space-y-2">
                  <Button type="button" onClick={handleGeneratePost}
                    disabled={!previewImage || postGenBusy} className="w-full">
                    {postGenBusy ? 'Генерация…' : 'Сгенерировать пост'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* REELS. */}
        <TabsContent value="reel" forceMount className="data-[state=inactive]:hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Настройки шаблона Reels</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <PostTemplateSettings
                    key={`reel-${formKey}`}
                    initialData={reelInitial}
                    onDataChange={setReelSettings}
                    showReelFrameUpload
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="lg:sticky lg:top-[72px] lg:self-start">
              <CardHeader>
                <CardTitle>Превью Reels (9:16)</CardTitle>
                <CardDescription>Загрузите тестовое видео, чтобы увидеть оверлей</CardDescription>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleReelVideoChange}
                  id="reelTestVideoUpload"
                  className="hidden"
                />
                <ReelPreview
                  key={`reel-${formKey}`}
                  text="Пример заголовка"
                  previewOnly
                  fontSize={reelSettings.formData.fontSize ?? null}
                  videoFile={reelTestVideo}
                  logoFile={reelSettings.logoFile}
                  logoSrc={reelSettings.logoPreview}
                  frameSrc={reelSettings.framePreview || reelSettings.formData.frameUrl}
                  textAlign={reelSettings.formData.textAlign}
                  textBoxStyle={reelSettings.formData.textBoxStyle}
                  textBoxAngle={reelSettings.formData.textBoxAngle}
                  textBoxRadius={reelSettings.formData.textBoxRadius}
                  textBoxPadding={reelSettings.formData.textBoxPadding}
                  textBoxPaddingTop={reelSettings.formData.textBoxPaddingTop}
                  textBoxPaddingLeft={reelSettings.formData.textBoxPaddingLeft}
                  textBoxPaddingRight={reelSettings.formData.textBoxPaddingRight}
                  onVideoUploadClick={() => document.getElementById('reelTestVideoUpload')?.click()}
                  backgroundColor={reelSettings.formData.backgroundColor}
                  backgroundAlpha={reelSettings.formData.backgroundAlpha}
                  textColor={reelSettings.formData.textColor}
                  useTwoColors={reelSettings.formData.useTwoColors}
                  textColorSecondary={reelSettings.formData.textColorSecondary}
                  fontFamily={reelSettings.fontFamily}
                  fontUrl={reelSettings.fontUrl}
                  textUppercase={reelSettings.formData.textUppercase}
                  fontVariationSettings={reelSettings.fontVariationSettings}
                  logoPosition={reelSettings.logoPosition as any}
                  textShadowEnabled={reelSettings.formData.textShadowEnabled}
                  textShadowColor={reelSettings.formData.textShadowColor}
                  textShadowBlur={reelSettings.formData.textShadowBlur}
                  textShadowWidth={reelSettings.formData.textShadowWidth}
                  textSoftShadowEnabled={reelSettings.formData.textSoftShadowEnabled}
                  textSoftShadowColor={reelSettings.formData.textSoftShadowColor}
                  textSoftShadowBlur={reelSettings.formData.textSoftShadowBlur}
                  textSoftShadowOffsetX={reelSettings.formData.textSoftShadowOffsetX}
                  textSoftShadowOffsetY={reelSettings.formData.textSoftShadowOffsetY}
                  textSoftShadowOpacity={reelSettings.formData.textSoftShadowOpacity}
                  textShadowOpacity={reelSettings.formData.textShadowOpacity}
                  logoSize={reelSettings.formData.logoSize}
                  textLineSpacing={reelSettings.formData.textLineSpacing}
                  reelTextCenterX={reelTextAnchor.centerX}
                  reelTextBottom={reelTextAnchor.bottom}
                  onTextPositionChange={handleReelTextPositionChange}
                />
                <PreviewCaption
                  size="1080×1920"
                  fontName={reelSettings.fontName}
                  fontSize={reelSettings.formData.fontSize}
                  backgroundAlpha={reelSettings.formData.backgroundAlpha}
                  safeZoneHint="Нижнюю часть кадра перекроют подпись и кнопки Instagram — не ставьте туда текст"
                />
                <div className="mt-3 space-y-2">
                  <Button type="button" onClick={handleGenerateReel}
                    disabled={!reelTestVideo || !reelTextPosition || reelGenPct !== null} className="w-full">
                    {reelGenPct !== null ? `Генерация… ${reelGenPct}%` : 'Сгенерировать рилс'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Enter в поле формы тоже сохраняет — скрытая кнопка внутри формы. */}
      <button type="submit" className="hidden" disabled={isUploading} aria-hidden />
    </form>
  );
}
